import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRecruit, getFormMetadata, type FormMetadata } from '../api/recruits';

const Field = ({
  label,
  required,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="block">
    <div className="text-xs uppercase tracking-wider text-dg-mist mb-1.5">
      {label}
      {required ? <span className="text-dg-red ml-1">*</span> : null}
    </div>
    {children}
    {hint ? <div className="text-[11px] text-dg-mist/70 mt-1">{hint}</div> : null}
  </label>
);

const inputCls =
  'w-full bg-dg-panel border border-dg-border rounded-md px-3 py-2 text-sm text-white placeholder-dg-mist/60 focus:outline-none focus:border-dg-red/60';

export default function RecruitingApplicationNew() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<FormMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('');
  const [licensedStatus, setLicensedStatus] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [compPercent, setCompPercent] = useState<number | ''>('');
  const [salesManagerId, setSalesManagerId] = useState('');
  const [directUplineName, setDirectUplineName] = useState('');
  const [directUplineEmail, setDirectUplineEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [tcpaConsent, setTcpaConsent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setMeta(await getFormMetadata());
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load form metadata');
      }
    })();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDuplicateId(null);
    if (!tcpaConsent) {
      setError('You must accept the TCPA consent to submit.');
      return;
    }
    if (compPercent === '') {
      setError('Comp % is required.');
      return;
    }
    setSubmitting(true);
    try {
      const { recruit } = await createRecruit({
        firstName,
        lastName,
        cellPhone,
        email,
        state,
        licensedStatus,
        leadSource,
        compPercent: Number(compPercent),
        salesManagerId,
        directUplineName,
        directUplineEmail,
        notes,
        tcpaConsent: true,
        tcpaConsentText: meta?.consentText || '',
      });
      navigate(`/recruiting/recruits/${recruit._id}?created=1`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string; code?: string; existingId?: string } } } };
      const apiErr = err.response?.data?.error;
      if (apiErr?.code === 'DUPLICATE' && apiErr.existingId) {
        setDuplicateId(apiErr.existingId);
        setError('A recruit with this contact info already exists.');
      } else {
        setError(apiErr?.message || 'Submission failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="h-display text-3xl mb-1">New Application</h1>
        <p className="text-dg-mist text-sm mb-8">
          Filled by a hiring manager on behalf of a candidate. Comp tier options
          are server-restricted to your max.
        </p>

        {!meta ? (
          <div className="text-dg-mist">Loading form…</div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 bg-dg-panel/60 border border-dg-border rounded-lg p-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name" required>
                <input className={inputCls} placeholder="New Hire's First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </Field>
              <Field label="Last Name" required>
                <input className={inputCls} placeholder="New Hire's Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </Field>
              <Field label="Cell Phone Number" required>
                <input type="tel" className={inputCls} placeholder="+15551234567" value={cellPhone} onChange={(e) => setCellPhone(e.target.value)} required />
              </Field>
              <Field label="Email" required>
                <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
              </Field>
              <Field label="State" required>
                <select className={inputCls} value={state} onChange={(e) => setState(e.target.value)} required>
                  <option value="">Select state…</option>
                  {meta.states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Licensed or Not?" required>
                <select className={inputCls} value={licensedStatus} onChange={(e) => setLicensedStatus(e.target.value)} required>
                  <option value="">Select…</option>
                  {meta.licensedOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Lead Source">
                <input className={inputCls} placeholder="How was the candidate found?" value={leadSource} onChange={(e) => setLeadSource(e.target.value)} />
              </Field>
              <Field label="Comp % Rate" required hint={`Your max: ${meta.maxCompPercent}%`}>
                <select className={inputCls} value={compPercent} onChange={(e) => setCompPercent(e.target.value === '' ? '' : Number(e.target.value))} required>
                  <option value="">Select tier…</option>
                  {meta.compTiers.map((t) => (
                    <option key={t} value={t}>{t}%</option>
                  ))}
                </select>
              </Field>
              <Field label="Sales Manager" required>
                <select className={inputCls} value={salesManagerId} onChange={(e) => setSalesManagerId(e.target.value)} required>
                  <option value="">Select manager…</option>
                  {meta.salesManagers.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Direct Upline (Full Name)" required>
                <input className={inputCls} value={directUplineName} onChange={(e) => setDirectUplineName(e.target.value)} required />
              </Field>
              <Field label="Direct Upline Email" required>
                <input type="email" className={inputCls} value={directUplineEmail} onChange={(e) => setDirectUplineEmail(e.target.value)} required />
              </Field>
            </div>
            <Field label="Notes">
              <textarea rows={4} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>

            <label className="flex gap-3 items-start text-sm bg-dg-surface/60 p-3 rounded-md border border-dg-border">
              <input
                type="checkbox"
                className="mt-1 accent-dg-red"
                checked={tcpaConsent}
                onChange={(e) => setTcpaConsent(e.target.checked)}
                required
              />
              <span className="text-dg-text-2">{meta.consentText}</span>
            </label>

            {error && (
              <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
                {error}
                {duplicateId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/recruiting/recruits/${duplicateId}`)}
                    className="ml-2 underline hover:text-white"
                  >
                    Open existing dossier →
                  </button>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-dg-border">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="border border-white/20 hover:bg-white/5 text-sm px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!tcpaConsent || submitting}
                className="bg-dg-red hover:bg-dg-red-hover disabled:bg-dg-red/40 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-md transition"
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
            </div>

          </form>
        )}
    </div>
  );
}
